import { ActionIcon, Button, Collapse, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlayerPauseFilled, IconPlayerPlayFilled, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MemoryRecordingDto, MemoryTranscriptStatus } from 'src/Services/API/Memories.api';
import { trackMemoryRecordingPlayed } from 'src/Services/Mixpanel/events';
import { formatVoiceDuration } from 'src/Utils/memoryDate';

import styles from './VoiceStoryPlayer.module.scss';

interface Props {
  recording: MemoryRecordingDto;
  onDelete: (recordingId: string) => void;
}

/** One voice story: a big play button (a native <audio> stays hidden behind it). */
const VoiceStoryPlayer = ({ recording, onDelete }: Props) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [transcriptOpened, setTranscriptOpened] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnd = () => setIsPlaying(false);
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('pause', onEnd);
    return () => {
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('pause', onEnd);
    };
  }, []);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      return;
    }
    await audio.play();
    setIsPlaying(true);
    trackMemoryRecordingPlayed({ duration_seconds: recording.durationSeconds });
  };

  const confirmDelete = () =>
    modals.openConfirmModal({
      title: t('memories.recording.deleteTitle', 'Retirer ce récit ?'),
      children: (
        <Text size="sm">
          {t(
            'memories.recording.deleteMessage',
            'La voix de {{name}} et sa transcription ne seront plus dans ce souvenir.',
            { name: recording.speakerDisplayName }
          )}
        </Text>
      ),
      labels: {
        confirm: t('memories.recording.deleteConfirm', 'Retirer'),
        cancel: t('common.cancel', 'Annuler'),
      },
      confirmProps: { color: 'red' },
      onConfirm: () => onDelete(recording.id),
    });

  if (recording.withdrawn) {
    return (
      <div className={styles.container}>
        <Text c="dimmed">
          {t('memories.recording.withdrawn', 'Récit retiré')} — {recording.speakerDisplayName}
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.main}>
        <ActionIcon
          size={72}
          radius="xl"
          variant="filled"
          onClick={togglePlay}
          disabled={!recording.audioUrl}
          aria-label={
            isPlaying
              ? t('memories.recording.pause', 'Mettre en pause')
              : t('memories.recording.play', 'Écouter')
          }
        >
          {isPlaying ? <IconPlayerPauseFilled size={32} /> : <IconPlayerPlayFilled size={32} />}
        </ActionIcon>
        <div className={styles.meta}>
          <span className={styles.speaker}>{recording.speakerDisplayName}</span>
          <span className={styles.details}>
            {formatVoiceDuration(recording.durationSeconds)} ·{' '}
            {t('memories.recording.recordedOn', 'enregistré le {{date}}', {
              date: dayjs(recording.recordedAt).format('DD/MM/YYYY'),
            })}
          </span>
        </div>
        {recording.canDelete && (
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            onClick={confirmDelete}
            aria-label={t('memories.recording.deleteTitle', 'Retirer ce récit ?')}
          >
            <IconTrash size={20} />
          </ActionIcon>
        )}
      </div>
      {recording.audioUrl && (
        <audio ref={audioRef} src={recording.audioUrl} preload="none" className={styles.audio} />
      )}

      {recording.transcriptStatus === MemoryTranscriptStatus.Pending && (
        <Text size="sm" c="dimmed">
          {t('memories.recording.transcribing', 'Transcription en cours…')}
        </Text>
      )}
      {recording.transcriptStatus === MemoryTranscriptStatus.Failed && (
        <Text size="sm" c="dimmed">
          {t('memories.recording.transcriptFailed', 'Transcription indisponible')}
        </Text>
      )}
      {recording.transcriptStatus === MemoryTranscriptStatus.Done && recording.transcript && (
        <>
          <Button
            variant="subtle"
            size="compact-md"
            className={styles.transcriptToggle}
            onClick={() => setTranscriptOpened(value => !value)}
          >
            {transcriptOpened
              ? t('memories.recording.hideTranscript', 'Masquer le texte')
              : t('memories.recording.showTranscript', 'Lire le texte')}
          </Button>
          <Collapse in={transcriptOpened}>
            <Text className={styles.transcript}>{recording.transcript}</Text>
          </Collapse>
        </>
      )}
    </div>
  );
};

export default VoiceStoryPlayer;
